'use client';

import { faMapLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FC, useState } from 'react';
import { Icon } from '@/components/ui';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { cn } from '@/utils';
import {
  googleMapsViewUrlFromEmbed,
  normalizeGoogleMapsEmbedUrl,
} from '@/utils/normalize-google-maps-embed-url';

type GoogleMapEmbedProps = {
  mapEmbedUrl: string;
  className?: string;
  minHeightClassName?: string;
};

const GoogleMapEmbed: FC<GoogleMapEmbedProps> = ({
  mapEmbedUrl,
  className,
  minHeightClassName = 'min-h-[420px]',
}) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);

  const embedSrc = normalizeGoogleMapsEmbedUrl(mapEmbedUrl);
  const viewUrl = googleMapsViewUrlFromEmbed(mapEmbedUrl);
  const mapTitle = t?.common.contactForm.mapTitle ?? 'Store location map';
  const loadMapLabel = t?.common.contactForm.loadMap ?? 'Load map';
  const openInMapsLabel = t?.common.contactForm.openInMaps ?? 'Open in Google Maps';

  if (!embedSrc) {
    if (!viewUrl) return null;

    return (
      <a
        href={viewUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'bg-default-100 text-foreground flex w-full items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium underline',
          minHeightClassName,
          className,
        )}
      >
        <Icon icon={faMapLocationDot} aria-hidden />
        {openInMapsLabel}
      </a>
    );
  }

  if (!isLoaded) {
    return (
      <button
        type="button"
        onClick={() => setIsLoaded(true)}
        className={cn(
          'border-default-200 bg-default-50 text-foreground hover:bg-default-100 flex w-full flex-col items-center justify-center gap-3 rounded-lg border px-4 text-center text-sm font-medium transition-colors',
          minHeightClassName,
          className,
        )}
        aria-label={loadMapLabel}
      >
        <Icon icon={faMapLocationDot} size="lg" aria-hidden />
        <span>{loadMapLabel}</span>
      </button>
    );
  }

  return (
    <iframe
      src={embedSrc}
      title={mapTitle}
      loading="lazy"
      className={cn('h-full w-full rounded-lg', minHeightClassName, className)}
      allowFullScreen
      referrerPolicy="no-referrer-when-downgrade"
    />
  );
};

export default GoogleMapEmbed;
